/**
 * Shared conventions for the queue/log tables.
 */

/**
 * Placeholder for a table cell with no value to render — either the API left the
 * field empty, or the column doesn't apply to that row (patient/MRN on an
 * activity-log admin-action row).
 *
 * Shared by the linking-requests queue, the dashboard queues, and the activity
 * log, which each used to declare their own copy: three literals of a character
 * that is easy to mistype as a hyphen or an en dash, in three tables staff read
 * side by side. Note this is a *cell* placeholder — it is not an accessible
 * name, so row/cell `aria-label`s drop the absent clause instead of announcing
 * the dash (see `buildRowAriaLabel` in the queues).
 */
export const EMPTY_CELL = '—';

/**
 * Compose a queue row's announcement: the `action` clause, then one clause per
 * column in reading order, with absent clauses (`null`) dropped.
 *
 * An `aria-label` on a `role="row"` *replaces* its accessible name, so the label
 * has to carry the columns a sighted user reads at a glance. Without them every
 * row whose subject name is missing announces as a bare "Open request", with no
 * date of birth, identifier, assignee, or status to tell it from the next one.
 * Absent clauses are dropped rather than announced as the {@link EMPTY_CELL}
 * dash — the dash is a visual affordance, not something worth reading aloud.
 *
 * Callers own the clause wording (the columns differ per queue) and should leave
 * out any column that renders as elapsed time ("3 hrs ago"): that is recomputed
 * at render, so a clause baked in at map time drifts from the cell as the row
 * ages.
 *
 * @param action Leading clause naming the action and its subject, e.g.
 *   `Open request for Sarah Thompson` — or just `Open request` when the row
 *   carries no name.
 * @param columns One clause per remaining column; `null` for a column this row
 *   has no value in.
 */
export function buildQueueRowAriaLabel(action: string, columns: (string | null)[]): string {
  return [action, ...columns].filter(Boolean).join(', ');
}
